import React from "react";
import { Autocomplete, Box, Checkbox, TextField } from "@mui/material";
import {
  DEGREE_OPTIONS,
  EXPERIENCE_OPTIONS,
  LOCATION_OPTIONS,
  PREVIOUS_COMPANY_OPTIONS,
} from "../../utils/constants";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import { setHrAgentFilters } from "../../store/filtersSlice";

type RefinementFiltersProps = {
  agentId?: string;
  disabled?: boolean;
};

/**
 * RefinementFilters - Candidate refinement filters for recruiter agent (18).
 * Filters are sent with the API request when agentId is 18 and values are selected.
 */
const RefinementFilters: React.FC<RefinementFiltersProps> = ({
  agentId,
  disabled = false,
}) => {
  if (agentId !== "18") return null;

  const dispatch = useAppDispatch();
  const filters = useSelector(
    (state: RootState) => state.filters.hrAgentFilters
  );

  const dropdownPopperSx = {
    width: "max-content",
    minWidth: 130,
    maxWidth: "80vw",
  } as const;

  // Styles applied to the dropdown menu (Paper). These must live here because the menu is rendered in a portal.
  const dropdownPaperSx = {
    ...dropdownPopperSx,
    "& .MuiAutocomplete-listbox": {
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },
    "& .MuiAutocomplete-option": {
      fontSize: "11px",
      lineHeight: 1.2,
      minHeight: 20,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 2,
      paddingRight: 2,
      whiteSpace: "normal",
    },
    "& .MuiCheckbox-root": {
      padding: 0,
      marginRight: 0,
    },
    "& .MuiSvgIcon-root": {
      fontSize: 14,
    },
  } as const;

  const dropdownInputSx = {
    "& .MuiInputBase-input": { fontSize: "11px !important" },
  } as const;

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <Box
        component="span"
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: "rgba(0,0,0,0.75)",
          whiteSpace: "nowrap",
          mr: 0.5,
        }}
      >
        Required Qualifications
      </Box>
      <Autocomplete
        size="small"
        options={EXPERIENCE_OPTIONS}
        value={filters.experience}
        disabled={disabled}
        componentsProps={{
          popper: {
            placement: "top-start",
            sx: {
              ...dropdownPopperSx,
            },
          },
          paper: {
            sx: {
              ...dropdownPaperSx,
            },
          },
        }}
        onChange={(_, newValue) =>
          dispatch(
            setHrAgentFilters({
              ...filters,
              experience: newValue,
            })
          )
        }
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Experience"
            variant="outlined"
            sx={dropdownInputSx}
          />
        )}
        sx={{
          width: 130,
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? "none" : "auto",
          "& .MuiInputBase-root": {
            height: 26,
            paddingTop: "3px !important",
            width: 130,
            paddingRight: "28px !important",
            "& .MuiAutocomplete-input": {
              fontSize: "11px !important",
            },
          },
        }}
      />

      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        options={LOCATION_OPTIONS}
        value={filters.location}
        disabled={disabled}
        componentsProps={{
          popper: {
            placement: "top-start",
            sx: {
              ...dropdownPopperSx,
            },
          },
          paper: {
            sx: {
              ...dropdownPaperSx,
            },
          },
        }}
        onChange={(_, newValue) =>
          dispatch(setHrAgentFilters({ ...filters, location: newValue }))
        }
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderOption={(props, option, { selected }) => {
          const { key, ...restProps } = props;

          return (
            <li key={key} {...restProps}>
              <Checkbox size="small" checked={selected} />
              {option.label}
            </li>
          );
        }}
        renderTags={(value) => {
          if (value.length === 0) return null;
          return (
            <Box
              component="span"
              sx={{
                fontSize: "11px",
                color: disabled ? "rgba(0,0,0,0.38)" : "rgba(0,0,0,0.87)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                pointerEvents: "none",
                position: "absolute",
                left: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                maxWidth: "calc(100% - 60px)",
                display: "block",
              }}
            >
              {value.length === 1 ? value[0].label : `${value.length} selected`}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={filters.location.length === 0 ? "Location" : ""}
            variant="outlined"
            sx={dropdownInputSx}
          />
        )}
        sx={{
          width: 130,
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? "none" : "auto",
          "& .MuiInputBase-root": {
            position: "relative",
            height: 26,
            paddingTop: "3px !important",
            width: 130,
            paddingRight: "28px !important",
            "& .MuiAutocomplete-input": {
              fontSize: "11px !important",
              opacity: filters.location.length > 0 ? 0 : 1,
            },
          },
        }}
      />

      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        options={DEGREE_OPTIONS}
        value={filters.degree}
        disabled={disabled}
        componentsProps={{
          popper: {
            placement: "top-start",
            sx: {
              ...dropdownPopperSx,
            },
          },
          paper: {
            sx: {
              ...dropdownPaperSx,
            },
          },
        }}
        onChange={(_, newValue) =>
          dispatch(setHrAgentFilters({ ...filters, degree: newValue }))
        }
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderOption={(props, option, { selected }) => {
          const { key, ...restProps } = props;

          return (
            <li key={key} {...restProps}>
              <Checkbox size="small" checked={selected} />
              {option.label}
            </li>
          );
        }}
        renderTags={(value) => {
          if (value.length === 0) return null;
          return (
            <Box
              component="span"
              sx={{
                fontSize: "11px",
                color: disabled ? "rgba(0,0,0,0.38)" : "rgba(0,0,0,0.87)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                pointerEvents: "none",
                position: "absolute",
                left: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                maxWidth: "calc(100% - 60px)",
                display: "block",
              }}
            >
              {value.length === 1 ? value[0].label : `${value.length} selected`}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={filters.degree.length === 0 ? "Degree" : ""}
            variant="outlined"
            sx={dropdownInputSx}
          />
        )}
        sx={{
          width: 130,
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? "none" : "auto",
          "& .MuiInputBase-root": {
            position: "relative",
            height: 26,
            paddingTop: "3px !important",
            paddingRight: "28px !important",
            "& .MuiAutocomplete-input": {
              fontSize: "11px !important",
              opacity: filters.degree.length > 0 ? 0 : 1,
            },
          },
        }}
      />

      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        options={PREVIOUS_COMPANY_OPTIONS}
        value={filters.previousCompany}
        disabled={disabled}
        componentsProps={{
          popper: {
            placement: "top-start",
            sx: {
              ...dropdownPopperSx,
            },
          },
          paper: {
            sx: {
              ...dropdownPaperSx,
            },
          },
        }}
        onChange={(_, newValue) =>
          dispatch(
            setHrAgentFilters({
              ...filters,
              previousCompany: newValue,
            })
          )
        }
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderOption={(props, option, { selected }) => {
          const { key, ...restProps } = props;

          return (
            <li key={key} {...restProps}>
              <Checkbox size="small" checked={selected} />
              {option.label}
            </li>
          );
        }}
        renderTags={(value) => {
          if (value.length === 0) return null;
          return (
            <Box
              component="span"
              sx={{
                fontSize: "11px",
                color: disabled ? "rgba(0,0,0,0.38)" : "rgba(0,0,0,0.87)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                pointerEvents: "none",
                position: "absolute",
                left: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                maxWidth: "calc(100% - 60px)",
                display: "block",
              }}
            >
              {value.length === 1 ? value[0].label : `${value.length} selected`}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              filters.previousCompany.length === 0 ? "Previous Company" : ""
            }
            variant="outlined"
            sx={dropdownInputSx}
          />
        )}
        sx={{
          width: 130,
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? "none" : "auto",
          "& .MuiInputBase-root": {
            position: "relative",
            height: 26,
            paddingTop: "3px !important",
            width: 140,
            paddingRight: "28px !important",
            "& .MuiAutocomplete-input": {
              fontSize: "11px !important",
              opacity: filters.previousCompany.length > 0 ? 0 : 1,
            },
          },
        }}
      />
    </Box>
  );
};

export default RefinementFilters;
